const SYSTEM_BASEMAP_SOURCE_ID = 'system-basemap';
const SYSTEM_BASEMAP_SPRITE_PATH_SEGMENT = '/api/v1/system/basemaps/sprites/';
const SYSTEM_BASEMAP_FONT_PATH_SEGMENT = '/api/v1/system/basemaps/fonts/';
const OVERLAY_LAYER_TYPES_TO_STRIP = new Set(['background', 'sky']);
const OVERLAY_ROOT_FIELDS_TO_STRIP = ['fog', 'light', 'projection', 'terrain'] as const;
const OVERLAY_ID_PREFIX = 'overlay-';

const cloneJson = <T,>(value: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

const asObjectRecord = (value: unknown) =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const buildUniqueOverlayId = (id: string, occupiedIds: Set<string>, usedIds: Set<string>) => {
  const aliasBase = id.startsWith(OVERLAY_ID_PREFIX) ? id : `${OVERLAY_ID_PREFIX}${id}`;
  let nextId = aliasBase;
  let counter = 1;
  while (occupiedIds.has(nextId) || usedIds.has(nextId)) {
    nextId = `${aliasBase}-${counter}`;
    counter += 1;
  }
  usedIds.add(nextId);
  return nextId;
};

const buildUniqueOverlaySourceId = (id: string, occupiedIds: Set<string>, usedIds: Set<string>) => {
  const aliasBase = id.startsWith(OVERLAY_ID_PREFIX) ? id : `${OVERLAY_ID_PREFIX}${id}`;
  let nextId = aliasBase;
  let counter = 1;
  while (occupiedIds.has(nextId) || usedIds.has(nextId) || nextId === SYSTEM_BASEMAP_SOURCE_ID) {
    nextId = `${aliasBase}-${counter}`;
    counter += 1;
  }
  usedIds.add(nextId);
  return nextId;
};

export const normalizeOverlayStyle = (styleJson?: Record<string, unknown> | null) => {
  if (!styleJson) {
    return null;
  }

  const nextStyle = cloneJson(styleJson);
  const styleSources = asObjectRecord(nextStyle.sources);
  nextStyle.sources = Object.fromEntries(
    Object.entries(styleSources ?? {}).filter(([sourceId]) => sourceId !== SYSTEM_BASEMAP_SOURCE_ID),
  );

  const rawLayers = Array.isArray(nextStyle.layers) ? (nextStyle.layers as Array<Record<string, unknown>>) : [];
  const normalizedLayers = rawLayers.filter(layer => {
    const source = typeof layer.source === 'string' ? layer.source : '';
    const metadata = asObjectRecord(layer.metadata);
    const type = typeof layer.type === 'string' ? layer.type : '';
    return (
      source !== SYSTEM_BASEMAP_SOURCE_ID &&
      metadata?.system_basemap !== true &&
      !OVERLAY_LAYER_TYPES_TO_STRIP.has(type)
    );
  });
  const keptLayerIds = new Set(
    normalizedLayers.map(layer => (typeof layer.id === 'string' ? layer.id : '')).filter(Boolean),
  );
  nextStyle.layers = normalizedLayers.filter(layer => {
    const ref = typeof layer.ref === 'string' ? layer.ref : '';
    return !ref || keptLayerIds.has(ref);
  });

  const sprite = typeof nextStyle.sprite === 'string' ? nextStyle.sprite : '';
  if (sprite.includes(SYSTEM_BASEMAP_SPRITE_PATH_SEGMENT)) {
    delete nextStyle.sprite;
  }
  const glyphs = typeof nextStyle.glyphs === 'string' ? nextStyle.glyphs : '';
  if (glyphs.includes(SYSTEM_BASEMAP_FONT_PATH_SEGMENT)) {
    delete nextStyle.glyphs;
  }

  OVERLAY_ROOT_FIELDS_TO_STRIP.forEach(field => {
    delete nextStyle[field];
  });

  return nextStyle;
};

export const prepareOverlayStyleForComposition = ({
  basemapLayerIds,
  basemapSourceIds,
  overlayStyle,
}: {
  basemapLayerIds: Iterable<string>;
  basemapSourceIds: Iterable<string>;
  overlayStyle: Record<string, unknown>;
}) => {
  const normalizedOverlay = normalizeOverlayStyle(overlayStyle) ?? {
    version: 8,
    sources: {},
    layers: [],
  };

  const nextStyle = cloneJson(normalizedOverlay);
  const occupiedSourceIds = new Set(Array.from(basemapSourceIds).filter(Boolean));
  const usedSourceIds = new Set<string>();
  const sourceIdMap = new Map<string, string>();
  const overlaySources = asObjectRecord(nextStyle.sources) ?? {};
  const remappedSources = Object.fromEntries(
    Object.entries(overlaySources).map(([sourceId, sourceValue]) => {
      const nextSourceId =
        occupiedSourceIds.has(sourceId) || sourceId === SYSTEM_BASEMAP_SOURCE_ID
          ? buildUniqueOverlaySourceId(sourceId, occupiedSourceIds, usedSourceIds)
          : sourceId;
      sourceIdMap.set(sourceId, nextSourceId);
      return [nextSourceId, sourceValue];
    }),
  );

  const occupiedLayerIds = new Set(Array.from(basemapLayerIds).filter(Boolean));
  const usedLayerIds = new Set<string>();
  const layerIdMap = new Map<string, string>();
  const overlayLayers = Array.isArray(nextStyle.layers) ? (nextStyle.layers as Array<Record<string, unknown>>) : [];
  overlayLayers.forEach(layer => {
    const layerId = typeof layer.id === 'string' ? layer.id : '';
    if (!layerId) {
      return;
    }
    layerIdMap.set(layerId, buildUniqueOverlayId(layerId, occupiedLayerIds, usedLayerIds));
  });

  nextStyle.sources = remappedSources;
  nextStyle.layers = overlayLayers.map(layer => {
    const nextLayer = { ...layer };
    if (typeof nextLayer.source === 'string' && sourceIdMap.has(nextLayer.source)) {
      nextLayer.source = sourceIdMap.get(nextLayer.source);
    }
    if (typeof nextLayer.id === 'string' && layerIdMap.has(nextLayer.id)) {
      nextLayer.id = layerIdMap.get(nextLayer.id);
    }
    if (typeof nextLayer.ref === 'string' && layerIdMap.has(nextLayer.ref)) {
      nextLayer.ref = layerIdMap.get(nextLayer.ref);
    }
    return nextLayer;
  });

  return nextStyle;
};
